import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  Platform,
} from "react-native";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Button } from "react-native-paper";
import { Picker } from "react-native";
import { useToast } from "react-native-toast-notifications";
import { orderUsersModal } from "../../../models/orderUsersModal";
import {
  getAllOperations,
  updateProcessingPerson,
} from "../../../services/operationService";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { headers, mdHeaders, smHeaders } from "../utils/operationHeaders";
import Spinner from "../../../components/shared/Spinner";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

interface FormData {
  operationId: string;
  operationUser: string;
}

const OperationProcessingUserModal: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const { operationUsersList, isOperationUsersLoaded } = useSelector(
    (state: any) => state.operations
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );
  const toast = useToast();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      operationId: order._id,
      operationUser: order?.createdBy || "",
    },
  });

  const { isLargeDevice, isMediumDevice } = useContext(
    DeviceOrientationContext
  );

  useEffect(() => {
    setTableHeaders(
      isLargeDevice ? headers : isMediumDevice ? mdHeaders : smHeaders
    );
  }, []);

  const onSubmit = async (data: FormData) => {
    const payload: FormData = {
      operationId: data.operationId,
      operationUser: data.operationUser,
    };

    try {
      setLoading(true);

      await updateProcessingPerson(payload)
        .then(() => {
          toast.show("Processing Person Updated successfully", {
            type: "success",
          });
          onClose(); // Close modal after success
          getAllOperations(tableHeaders);
        })
        .catch(() => {
          toast.show("Error updating processing person", {
            type: "danger",
          });
        });
    } catch (err) {
      console.error("Processing Person update failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>
              Update Processing Person: {order?.OrderNumber1}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.inputLabel}>Select Person</Text>
          {loading || !isOperationUsersLoaded ? (
            <Spinner
              size="small"
              color="#74BBFF"
              styles={{ flexDirection: "row" }}
            />
          ) : (
            <>
              <Controller
                control={control}
                name="operationUser"
                rules={{ required: "Processing Person is required" }}
                render={({ field: { onChange, value } }) => (
                  <View
                    style={[
                      styles.pickerWrapper,
                      Platform.OS === "android" && {
                        borderWidth: 1.5,
                        borderColor: "#a6a6a6",
                        borderRadius: 10,
                      },
                    ]}
                  >
                    <Picker
                      selectedValue={value}
                      onValueChange={onChange}
                      style={styles.picker}
                    >
                      <Picker.Item
                        label="-- Select Processing Person --"
                        value=""
                      />
                      {operationUsersList?.map((user: orderUsersModal) => (
                        <Picker.Item
                          key={user._id}
                          label={user.display_name}
                          value={user._id}
                        />
                      ))}
                    </Picker>
                  </View>
                )}
              />
              {errors.operationUser && (
                <Text style={styles.errorStyle}>
                  {errors.operationUser.message}
                </Text>
              )}

              <Button
                mode="contained"
                onPress={handleSubmit(onSubmit)}
                style={styles.submitButton}
              >
                Submit
              </Button>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 25,
    width: 500,
    borderRadius: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    flexShrink: 1,
  },
  closeButton: {
    backgroundColor: "#1E88E5",
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  closeButtonText: {
    color: "#fff",
    fontSize: 16,
  },
  inputLabel: {
    fontWeight: "bold",
    marginBottom: 5,
    marginTop: 20,
    fontSize: 14,
  },
  pickerWrapper: {
    marginBottom: 10,
  },
  picker: {
    height: 50,
    width: "100%",
  },
  errorStyle: {
    color: "red",
    fontSize: 13,
    marginBottom: 10,
  },
  submitButton: {
    marginTop: 15,
    backgroundColor: "#000",
  },
});

export default OperationProcessingUserModal;
